/**
 * ч.1316: позначка «ще колонки →» над таблицями ag-Grid.
 *
 * У DataTable вона є з ч.1239 (SUP-321707CF); списки на ag-Grid (банк, каса, продажі, залишки,
 * акт звірки…) її не мали — зауваження власника «"ще колонки" не працює в Гроші». Рахуємо за
 * API сітки: ширина незакріплених колонок проти правого краю видимої частини. Клік прокручує
 * до останньої колонки.
 *
 * Використання: `const more = useAgMoreRight<T>()`; `{...more.gridProps}` на AgGridReact
 * (сторінка, що має власний onGridReady / onColumnResized, кличе `more.check(e.api)` у них
 * сама); `<GridMoreRightButton more={more} />` над таблицею.
 */
import { useRef, useState } from 'react';
import type {
  BodyScrollEvent, ColumnResizedEvent, DisplayedColumnsChangedEvent, FirstDataRenderedEvent,
  GridApi, GridReadyEvent, GridSizeChangedEvent,
} from 'ag-grid-community';

function centerColumns<T>(api: GridApi<T>) {
  return api.getAllDisplayedColumns().filter((c) => !c.getPinned());
}

export function useAgMoreRight<T>() {
  const apiRef = useRef<GridApi<T> | null>(null);
  const [moreRight, setMoreRight] = useState(false);
  const check = (api: GridApi<T> | null | undefined) => {
    if (!api || api.isDestroyed()) return;
    apiRef.current = api;
    const total = centerColumns(api).reduce((sum, c) => sum + c.getActualWidth(), 0);
    const { right } = api.getHorizontalPixelRange();
    setMoreRight(right > 0 && total - right > 8);
  };
  const scrollToLast = () => {
    const api = apiRef.current;
    if (!api || api.isDestroyed()) return;
    const cols = centerColumns(api);
    const last = cols[cols.length - 1];
    if (last) api.ensureColumnVisible(last, 'end');
  };
  const gridProps = {
    onGridReady: (e: GridReadyEvent<T>) => { window.setTimeout(() => check(e.api), 0); },
    onColumnResized: (e: ColumnResizedEvent<T>) => check(e.api),
    onBodyScroll: (e: BodyScrollEvent<T>) => { if (e.direction === 'horizontal') check(e.api); },
    onGridSizeChanged: (e: GridSizeChangedEvent<T>) => check(e.api),
    onDisplayedColumnsChanged: (e: DisplayedColumnsChangedEvent<T>) => check(e.api),
    onFirstDataRendered: (e: FirstDataRenderedEvent<T>) => check(e.api),
  };
  return { moreRight, check, scrollToLast, gridProps };
}

export function GridMoreRightButton({ more }: {
  more: { moreRight: boolean; scrollToLast: () => void };
}) {
  if (!more.moreRight) return null;
  return (
    <button type="button" className="data-grid__more" data-testid="grid-more-right"
      title="Таблиця ширша за екран: праворуч є ще колонки. Натисніть, щоб прокрутити до них"
      onClick={more.scrollToLast}>
      ще колонки →
    </button>
  );
}
